import type { Mode } from "@eldrun/content";
import {
  CHUNKS_PER_SIDE,
  computeWorldHash,
  concatResources,
  HEIGHT_SIDE,
  HEIGHT_SPACING,
  resourcesHash,
  type Heightfield,
  type PlanData,
  type ResourceSet,
} from "@eldrun/worldgen";
import type { ChunkBounds } from "./terrain-manager.ts";
import { blitTile, chunkKey } from "./tile.ts";
import { TerrainWorkerPool, wrapWorker } from "./worker-pool.ts";

export interface LoadedWorld {
  seed: number;
  mode: Mode;
  pool: TerrainWorkerPool;
  plan: PlanData;
  heightfield: Heightfield;
  bounds: Map<number, ChunkBounds>;
  worldHash: number;
  /** Recursos da ilha, gerados por chunk nos workers. */
  resources: ResourceSet;
  resourceHash: number;
  loadMs: number;
}

export function workerCount(): number {
  return Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1));
}

/**
 * Gera a ilha no cliente: planeja num worker, gera os 256 tiles em paralelo nos workers e monta
 * o heightfield; o `worldHash` resultante é comparado com o do servidor.
 */
export async function loadWorld(
  seed: number,
  mode: Mode,
  onProgress: (fraction: number, label: string) => void = () => {},
  signal?: AbortSignal,
): Promise<LoadedWorld> {
  const started = performance.now();
  signal?.throwIfAborted();
  const pool = new TerrainWorkerPool(
    () => wrapWorker(new Worker(new URL("./terrain.worker.ts", import.meta.url), { type: "module" })),
    workerCount(),
  );
  // Cancelar encerra os workers: os pedidos pendentes rejeitam e nada vaza.
  signal?.addEventListener("abort", () => pool.terminate(), { once: true });
  onProgress(0.05, "Planejando a ilha…");
  const { plan } = await pool.plan(seed, mode);
  const heights = new Float32Array(HEIGHT_SIDE * HEIGHT_SIDE);
  const bounds = new Map<number, ChunkBounds>();
  const requests = [];
  for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) requests.push(pool.tile(cx, cz));
  let done = 0;
  for (const request of requests) {
    const tile = await request;
    blitTile(heights, HEIGHT_SIDE, tile.heights, tile.cx, tile.cz);
    bounds.set(chunkKey(tile.cx, tile.cz), { minY: tile.minY, maxY: tile.maxY });
    done++;
    if (done % 16 === 0) onProgress(0.1 + 0.85 * (done / requests.length), "Erguendo o terreno…");
  }
  signal?.throwIfAborted();
  const heightfield: Heightfield = { side: HEIGHT_SIDE, spacing: HEIGHT_SPACING, heights };
  const worldHash = computeWorldHash(plan, heightfield);

  onProgress(0.95, "Plantando a floresta…");
  await pool.setHeightfield(heights);
  const parts: ResourceSet[] = [];
  const resourceRequests = [];
  for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) resourceRequests.push(pool.resources(cx, cz));
  for (const request of resourceRequests) parts.push((await request).set);
  signal?.throwIfAborted();
  const resources = concatResources(parts);
  onProgress(1, "Ilha pronta.");
  return {
    seed,
    mode,
    pool,
    plan,
    heightfield,
    bounds,
    worldHash,
    resources,
    resourceHash: resourcesHash(resources),
    loadMs: Math.round(performance.now() - started),
  };
}
